/**
 * タブバー共通コンポーネント
 *
 * menu テーブルの子メニュー（type='tab'）を受け取り、
 * 各ページ上部に横並びタブを描画する。
 *
 * 使い方:
 *   1. 親メニューの code を指定
 *      <TabBar parentCode="tenant" />
 *   2. または現在URLから自動判定
 *      <TabBar />
 *   3. または任意の MenuItem[] を直接渡す
 *      <TabBar tabs={customTabs} />
 */

import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { useTabsByCode, useCurrentTabs } from "../../../shared/menu/useMenus";
import type { MenuItem } from "../../../shared/menu/menuTypes";

interface TabBarProps {
    /** 親メニューのcode (例: "tenant", "store", "employee") */
    parentCode?: string;
    /** 明示的にタブ配列を指定（parentCode より優先） */
    tabs?: MenuItem[];
    /** 追加クラス */
    className?: string;
}

export function TabBar({ parentCode, tabs, className }: TabBarProps) {
    const tabsByCode = useTabsByCode(parentCode ?? "");
    const currentTabs = useCurrentTabs();

    // 優先順位: 明示指定 > parentCode指定 > 現在URLから自動判定
    const resolvedTabs: MenuItem[] =
        tabs ?? (parentCode ? tabsByCode : currentTabs.tabs);

    if (resolvedTabs.length === 0) return null;

    return (
        <div className={clsx("flex-shrink-0 px-6 pb-5", className)}>
            <div className="flex items-stretch gap-1 rounded-lg bg-white px-4 h-[50px]">
                {resolvedTabs.map((tab) => {
                    // url が無いタブは未実装扱いでクリック不可・グレーアウト
                    if (!tab.url) {
                        return (
                            <span
                                key={tab.id}
                                className="grid grid-rows-[1fr_auto] place-items-center px-6 text-base font-medium text-gray-300 cursor-not-allowed"
                                title="未実装"
                            >
                                <span className="inline-block min-w-[3em] text-center">{tab.name}</span>
                                <span className="h-[3px] min-w-[3em] w-full rounded-full bg-transparent" />
                            </span>
                        );
                    }
                    return (
                        <NavLink
                            key={tab.id}
                            to={tab.url}
                            end
                            className="grid grid-rows-[1fr_auto] place-items-center px-6 text-base font-medium transition-colors"
                        >
                            {({ isActive }) => (
                                <>
                                    <span className={clsx(
                                        "inline-block min-w-[3em] text-center",
                                        isActive ? "text-brand-600" : "text-gray-900 hover:text-gray-700",
                                    )}>
                                        {tab.name}
                                    </span>
                                    <span className={clsx(
                                        "h-[3px] min-w-[3em] w-full rounded-full",
                                        isActive ? "bg-brand-500" : "bg-transparent",
                                    )} />
                                </>
                            )}
                        </NavLink>
                    );
                })}
            </div>
        </div>
    );
}
